import React, { useContext, useEffect, useState } from 'react';
import useStyles from './RemoveEsimsDialog.styles';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core';
import tPaths from '../../../../utils/tPaths';
import { useCallbackOne, useMemoOne } from 'use-memo-one';
import useSnackbar from '../../../../utils/useSnackbar';
import useApi from '../../../../utils/useApi';
import { endpoints } from '../../../../utils/endpoints';
import { TableStateContext } from '../../../../contexts/tableState/TableStateContext';
import { ETablePopups } from '../../../../types/enums/ETablePopups';
import { mapping } from '../../../../utils/jsxUtils';

interface IProps {
  tableName: string;
  refetchTable: () => void;
}

interface IUser {
  _id: string;
  name: string;
  email: string;
  selected: boolean;
}

function popupIsOpen(state: Record<string, any>, tableName: string) {
  return state[tableName].popups[ETablePopups.RemoveEsims]?.isOpen;
}

const RemoveEsimsDialog: React.FC<IProps> = ({ tableName, refetchTable }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const pushSnackbar = useSnackbar();
  const { get, post } = useApi(null);
  const { setPopup, state } = useContext(TableStateContext);
  const [users, setUsers] = useState<IUser[]>([]);

  const isOpen = useMemoOne(() => {
    return !!(state[tableName]?.popups ? popupIsOpen(state, tableName) : false);
  }, [state[tableName]?.popups]);

  useEffect(() => {
    if (isOpen)
      get({
        endpoint: endpoints.a1Subscribers,
        onSuccess: ({ data }) => {
          if (data.length) {
            setUsers(
              data.map((user: IUser) => ({
                _id: user._id,
                name: user.name,
                email: user.email,
                selected: false,
              })),
            );
          }
        },
        onError: (e) => console.log('A1 eror', e),
      });
  }, [get, tableName, isOpen]);

  const handleClose = useCallbackOne(() => {
    setPopup(tableName, ETablePopups.RemoveEsims, { isOpen: false });
  }, [setPopup, tableName]);

  const handleOnSelectUser = useCallbackOne((_id) => {
    setUsers((users: IUser[]) => {
      const userToUpdate = users.find((user: IUser) => user._id === _id);

      if (userToUpdate) userToUpdate.selected = !userToUpdate?.selected;

      return [...users];
    });
  }, []);

  const handleRemove = useCallbackOne(() => {
    const selectedUserdId: string[] = users.filter((user: IUser) => user.selected).map((user: IUser) => user._id);

    if (!selectedUserdId.length) {
      pushSnackbar({ message: t(tPaths.table.notSelectedUsers), severity: 'warning' });
      return;
    }

    post({
      endpoint: endpoints.removeEsims,
      body: { usersId: selectedUserdId },
      onSuccess: () => {
        pushSnackbar({ message: t(tPaths.table.successOnRemoveEsims), severity: 'success' });
        refetchTable();
        handleClose();
      },
      onError: () => {
        pushSnackbar({ message: t(tPaths.global.wentWrong), severity: 'error' });
      },
    });
  }, [users]);

  return (
    <Dialog open={isOpen} fullWidth={true} onBackdropClick={handleClose}>
      <DialogTitle>
        <Typography className={classes.title}>{t(tPaths.table.resetEsims)}</Typography>
      </DialogTitle>

      <DialogContent>
        {mapping<IUser>(
          users,
          (user) => (
            <User details={user} onCheckbox={handleOnSelectUser} />
          ),
          <Typography variant="h5">{t(tPaths.table.noOneEsim)}</Typography>,
        )}
      </DialogContent>

      <DialogActions>
        <Button className={classes.button} color="secondary" onClick={handleClose}>
          {t(tPaths.global.cancel)}
        </Button>
        <Button className={classes.button} onClick={handleRemove}>
          {t(tPaths.table.resetEsims)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface IUserProps {
  details: IUser;
  onCheckbox: (userId: string) => void;
}

const User = ({ details, onCheckbox }: IUserProps) => (
  <Box mt={1} display={'flex'} alignItems={'center'}>
    <Grid container spacing={1}>
      <Grid item xs={4}>
        <Box display={'flex'} alignItems={'center'} style={{ height: '100%' }}>
          {details.name}
        </Box>
      </Grid>

      <Grid item xs={7}>
        <Box display={'flex'} alignItems={'center'} style={{ height: '100%' }}>
          {details.email}
        </Box>
      </Grid>

      <Grid item xs={1}>
        <Checkbox checked={details.selected} onChange={() => onCheckbox(details._id)} />
      </Grid>
    </Grid>
  </Box>
);

export default RemoveEsimsDialog;
